// reflow-engine.ts
import { Reservation, RowType } from "src/app/models";

export type Flip = {
  id: string;
  rowType: RowType;
  rowId: string;
  newFlag: boolean;
};

export function rowReservationsOnLane(
  reservations: Reservation[],
  rowType: RowType,
  rowId: string,
  laneSecond: boolean
) {
  return reservations.filter(
    (r) =>
      r.rowType === rowType &&
      r.rowId === rowId &&
      !!r.secondPosition === laneSecond
  );
}

export function overlapsCountOnLane(
  reservations: Reservation[],
  rowType: RowType,
  rowId: string | null,
  startMin: number,
  endMin: number,
  laneSecond: boolean,
  excludeId?: string
): number {
  if (!rowId) return 0;
  return reservations.filter(
    (r) =>
      r.id !== excludeId &&
      r.rowType === rowType &&
      r.rowId === rowId &&
      !!r.secondPosition === laneSecond &&
      startMin < r.endMin &&
      endMin > r.startMin
  ).length;
}

export function decideSecondForRow(
  reservations: Reservation[],
  rowType: RowType,
  rowId: string,
  startMin: number,
  endMin: number,
  excludeId?: string
): boolean {
  const busyOnPrimary =
    overlapsCountOnLane(
      reservations,
      rowType,
      rowId,
      startMin,
      endMin,
      /*laneSecond=*/ false,
      excludeId
    ) > 0;
  return busyOnPrimary;
}

/** Applique secondPosition sur les 2 lignes d’un même id, renvoie un nouveau tableau */
export function setSecondForBothLines(
  reservations: Reservation[],
  resId: string,
  activeType: RowType,
  secondActive: boolean,
  secondOther?: boolean
): Reservation[] {
  return reservations.map((x) => {
    if (x.id !== resId) return x;
    if (x.rowType === activeType)
      return { ...x, secondPosition: !!secondActive };
    if (secondOther !== undefined)
      return { ...x, secondPosition: !!secondOther };
    return x;
  });
}

/** Reflow d’une *ligne* (rowType,rowId) - renvoie { next, flips } */
export function reflowRowStable(
  reservations: Reservation[],
  rowType: RowType,
  rowId: string,
  preferId?: string
): { next: Reservation[]; flips: Flip[] } {
  const items = reservations
    .filter((r) => r.rowType === rowType && r.rowId === rowId)
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  // --- FIX: gérer correctement 0 ou 1 item ---
  if (items.length === 0) {
    return { next: reservations, flips: [] };
  }
  if (items.length === 1) {
    const it = items[0];
    // S’il est en 2e position alors qu’il est seul → le promouvoir en 1re
    if (it.secondPosition) {
      const flips: Flip[] = [{ id: it.id, rowType, rowId, newFlag: false }];
      const next = reservations.map((r) =>
        r.rowType === rowType && r.rowId === rowId && r.id === it.id
          ? { ...r, secondPosition: false }
          : r
      );
      return { next, flips };
    }
    // déjà en 1re position → rien à faire
    return { next: reservations, flips: [] };
  }

  const overlap = (aS: number, aE: number, bS: number, bE: number) =>
    aS < bE && aE > bS;

  const lane1: { start: number; end: number; id: string }[] = [];
  const inLane1Now = new Set<string>();

  // garder ce qui tient déjà en lane1
  for (const it of items) {
    if (it.secondPosition) continue;
    const fits = !lane1.some((s) =>
      overlap(it.startMin, it.endMin, s.start, s.end)
    );
    if (fits) {
      lane1.push({ start: it.startMin, end: it.endMin, id: it.id });
      inLane1Now.add(it.id);
    }
  }

  // favoriser preferId si possible
  if (preferId) {
    const it = items.find((x) => x.id === preferId);
    if (it) {
      const already = inLane1Now.has(it.id) && !it.secondPosition;
      const fits = !lane1.some((s) =>
        overlap(it.startMin, it.endMin, s.start, s.end)
      );
      if (!already && fits) {
        lane1.push({ start: it.startMin, end: it.endMin, id: it.id });
        inLane1Now.add(it.id);
      }
    }
  }

  // remplir le reste de lane1
  for (const it of items) {
    if (inLane1Now.has(it.id)) continue;
    const fits = !lane1.some((s) =>
      overlap(it.startMin, it.endMin, s.start, s.end)
    );
    if (fits) {
      lane1.push({ start: it.startMin, end: it.endMin, id: it.id });
      inLane1Now.add(it.id);
    }
  }

  const nextFlag = new Map<string, boolean>();
  for (const it of items) nextFlag.set(it.id, !inLane1Now.has(it.id));

  const flips: Flip[] = [];
  const next = reservations.map((r) => {
    if (r.rowType === rowType && r.rowId === rowId) {
      const flag = nextFlag.get(r.id);
      if (flag !== undefined && !!r.secondPosition !== flag) {
        flips.push({ id: r.id, rowType, rowId, newFlag: flag });
        return { ...r, secondPosition: flag };
      }
    }
    return r;
  });

  return { next, flips };
}

/** Reflow cascade (ligne active + miroir) */
export function reflowCascadeStable(
  reservations: Reservation[],
  activeRowType: RowType,
  activeRowId: string,
  preferId?: string
): { next: Reservation[]; flips: Flip[] } {
  const { next: afterActive, flips: flipsActive } = reflowRowStable(
    reservations,
    activeRowType,
    activeRowId,
    preferId
  );

  const promotedIds = new Set(
    flipsActive
      .filter(
        (f) =>
          f.rowType === activeRowType &&
          f.rowId === activeRowId &&
          f.newFlag === false
      )
      .map((f) => f.id)
  );

  let acc = afterActive;
  const mirrorFlips: Flip[] = [];

  for (const id of promotedIds) {
    const mirrorEntry = acc.find(
      (x) => x.id === id && x.rowType !== activeRowType
    );
    if (!mirrorEntry) continue;
    const tmp = reflowRowStable(
      acc,
      mirrorEntry.rowType,
      mirrorEntry.rowId,
      id
    );
    acc = tmp.next;
    mirrorFlips.push(...tmp.flips);
  }

  return { next: acc, flips: [...flipsActive, ...mirrorFlips] };
}
